import { Menu } from 'lucide-react'
import { useState } from 'react'
import type { CurrentUserWithPermissions } from '@/shared/lib/workspace-permissions'
import type { OnlineWorkspacePresence } from '@/features/workspace-presence/model/use-workspace-presence'
import type {
  WorkspaceSwitcherPresenceUser,
  WorkspaceSwitcherWorkspace,
} from '@/features/workspace-presence/ui/workspace-presence-workspace-switcher'
import { AiChatAvatarButton } from '@/features/ai-chat/ai-chat-avatar-button'
import { PersonalInfoDropdown } from '@/app/shell/personal-info-dropdown'
import { ThemeToggle } from '@/app/shell/theme-toggle'
import { useWorkspacePresence } from '@/features/workspace-presence/model/use-workspace-presence'
import { WorkspaceSheet } from '@/features/workspace/ui/workspace-sheet'
import {
  getWorkspacePresenceAvatar,
  getWorkspacePresenceDisplayName,
} from '@/features/workspace-presence/model/workspace-presence'
import { useWorkspaceChangeAction } from '@/features/workspace/model/workspace-switching'
import { WorkspacePresenceWorkspaceSwitcher } from '@/features/workspace-presence/ui/workspace-presence-workspace-switcher'
import { Route as RootRoute } from '@/routes/__root'
import { NotificationsDropdown } from '@/shared/notifications/notification-center'
import { WorkspacePlanBrand } from '@/features/workspace-entitlements/ui/workspace-plan-brand'

interface HeaderProps {
  isNavPinned: boolean
  isDesktop: boolean
  onMenuClick: () => void
}

function buildWorkspaceSwitcherPresenceUsers(
  onlinePresence: Array<OnlineWorkspacePresence>,
): Array<WorkspaceSwitcherPresenceUser> {
  return onlinePresence.flatMap((presence) => {
    if (presence.status === 'offline') {
      return []
    }

    return [
      {
        avatar: getWorkspacePresenceAvatar(presence),
        displayName: getWorkspacePresenceDisplayName(presence),
        id: presence.userId,
        isMuted: presence.isMuted,
        status: presence.status,
      },
    ]
  })
}

function buildWorkspaceSwitcherWorkspaces({
  currentUser,
  onlinePresence,
}: {
  currentUser: CurrentUserWithPermissions | null
  onlinePresence: Array<OnlineWorkspacePresence>
}): Array<WorkspaceSwitcherWorkspace> {
  if (!currentUser) {
    return []
  }

  const onlineUsers = buildWorkspaceSwitcherPresenceUsers(onlinePresence)
  const availableWorkspaces = getAvailableWorkspaces(currentUser)

  return availableWorkspaces.map((workspace) => ({
    id: workspace.id,
    name: workspace.name,
    organizationId:
      'organizationId' in workspace ? workspace.organizationId : null,
    organizationName:
      'organizationName' in workspace ? workspace.organizationName : null,
    workspaceRole:
      'workspaceRole' in workspace ? workspace.workspaceRole : null,
    isReadOnly: 'isReadOnly' in workspace ? workspace.isReadOnly : false,
    onlineUsers: workspace.id === currentUser.workspaceId ? onlineUsers : [],
  }))
}

export default function Header({
  isNavPinned,
  isDesktop,
  onMenuClick,
}: HeaderProps) {
  const { currentUser } = RootRoute.useRouteContext()
  const [workspaceSheetOpen, setWorkspaceSheetOpen] = useState(false)
  const onlinePresence = useWorkspacePresence({ currentUser })
  const handleWorkspaceChange = useWorkspaceChangeAction(currentUser)

  const showMenuButton = !(isDesktop && isNavPinned)
  const workspaceSwitcherWorkspaces = buildWorkspaceSwitcherWorkspaces({
    currentUser,
    onlinePresence,
  })

  return (
    <>
      <header className="relative flex flex-wrap items-center gap-y-2 border-b bg-background bg-[radial-gradient(circle_at_top_left,color-mix(in_oklab,var(--color-primary)_12%,transparent),transparent_60%)] p-2 sm:p-4">
        {showMenuButton && (
          <button
            onClick={onMenuClick}
            className="rounded-lg p-1 transition-colors sm:p-2"
            aria-label="Open menu"
          >
            <Menu size={24} />
          </button>
        )}
        <h1 className="ml-2 shrink-0 text-xl font-semibold sm:ml-4">
          <WorkspacePlanBrand />
        </h1>
        {currentUser ? (
          <div className="order-2 min-w-0 basis-full sm:order-none sm:ml-3 sm:basis-auto">
            <WorkspacePresenceWorkspaceSwitcher
              currentWorkspaceId={currentUser.workspaceId}
              onAddWorkspace={() =>
                void handleWorkspaceChange('__add-workspace__', () =>
                  setWorkspaceSheetOpen(true),
                )
              }
              onWorkspaceChange={(workspaceId) =>
                void handleWorkspaceChange(workspaceId, () => {})
              }
              workspaces={workspaceSwitcherWorkspaces}
            />
          </div>
        ) : null}
        <div className="ml-auto flex shrink-0 items-center gap-1 sm:gap-2">
          <NotificationsDropdown />
          <ThemeToggle />
          <PersonalInfoDropdown />
          <AiChatAvatarButton />
        </div>
      </header>
      {workspaceSheetOpen ? (
        <WorkspaceSheet
          mode="create"
          open={workspaceSheetOpen}
          onOpenChange={setWorkspaceSheetOpen}
        />
      ) : null}
    </>
  )
}

function getAvailableWorkspaces(currentUser: CurrentUserWithPermissions) {
  if (currentUser.availableWorkspaces.length > 0) {
    return currentUser.availableWorkspaces
  }

  return currentUser.workspace ? [currentUser.workspace] : []
}
